import { PaginationState } from "@tanstack/react-table";
import classNames from "classnames";
import { format } from "date-fns";
import { Form, Formik, Field } from "formik";
import {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { searchByIin, searchOrganisationByName } from "../../common/api/authApi";
import MarksRegistryService, {
  MarkRegistryItem,
  MarkRegistrySearchRequest,
} from "../../common/api/marksRegistryApi";
import { getNtinCode } from "../../common/api/nktApi";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import Calendar from "../../shared/Ui/Calendar/Calendar";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import Loader from "../../shared/Ui/Loader/Loader";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import CopyButton from "../../shared/Ui/CopyButton/CopyButton";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import Pagination from "../../shared/components/Table/Pagination";
import { getMarkStatusType } from "../../shared/documentUtils/dictionary";
import { useAppSelector } from "../../shared/hook";
import QrScannerComponent from "../QrMarks/components/Qr/QrScannerComponent";
import style from "./MarkingCodes.module.css";

const OPERATOR_ROLE_ID = 5;
const MARK_CODE_LENGTH = 29;

type OrgOption = { value: string; label: string };

const MARKS_PAGE_SIZES = [30, 60, 90, 120, 150];
const MARKS_PAGE_SIZE_OPTIONS = MARKS_PAGE_SIZES.map((value) => ({
  value,
  label: String(value),
}));

const tdClass =
  "px-2 py-3 text-sm border-b border-gray-200 text-gray-600 whitespace-nowrap";
const thClass =
  "px-2 py-2 text-center text-sm text-gray-600 font-semibold bg-white border-b border-gray-200 max-w-[6.5rem] leading-snug whitespace-normal";
const tdCodeClass =
  "px-2 py-3 text-sm border-b border-gray-200 text-gray-600 text-left break-all min-w-[140px] max-w-[160px] leading-snug";
const COL_SPAN = 14;

type SelectOption = { value: string; label: string };

type FilterValues = {
  markCode: string;
  status: string[];
  kmTypes: string[];
  productTypeCode: string;
  productName: string;
  ntin: string;
  ntinName: string;
  orgIinBin: string;
  orgName: string;
};

const emptyFilters = (): FilterValues => ({
  markCode: "",
  status: [],
  kmTypes: [],
  productTypeCode: "",
  productName: "",
  ntin: "",
  ntinName: "",
  orgIinBin: "",
  orgName: "",
});

function formatKmType(kmType: string | undefined, t: (k: string) => string) {
  if (!kmType) return "—";
  const upper = String(kmType).toUpperCase();
  if (
    upper === "GROUP" ||
    upper.includes("ГРУПП") ||
    upper.includes("ТОПТ")
  ) {
    return t("marking_codes.km_type_group");
  }
  if (upper === "CONSUMER" || upper.includes("ПОТРЕБ") || upper.includes("ТҰТЫН")) {
    return t("marking_codes.km_type_consumer");
  }
  return kmType;
}

function isGroupMark(row: MarkRegistryItem) {
  const upper = String(row?.kmType || "").toUpperCase();
  return (
    upper === "GROUP" ||
    upper.includes("ГРУПП") ||
    upper.includes("ТОПТ") ||
    upper.includes("GROUP")
  );
}

function normalizeMulti(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "string") return item;
      if (typeof item === "object" && "value" in item) {
        const value = (item as { value?: unknown }).value;
        return value == null ? null : String(value);
      }
      return String(item);
    })
    .filter((item): item is string => !!item);
}

function buildSearchBody(
  filters: FilterValues,
  startDate?: Date,
  endDate?: Date,
  isOperator = false,
): MarkRegistrySearchRequest {
  const body: MarkRegistrySearchRequest = {};
  const code = filters.markCode.trim();
  if (code) body.markCode = code;
  const statuses = normalizeMulti(filters.status);
  if (statuses.length) body.status = statuses;
  const kmTypes = normalizeMulti(filters.kmTypes);
  if (kmTypes.length) body.kmTypes = kmTypes;
  const productTypeCode = filters.productTypeCode.trim();
  if (productTypeCode) body.productTypeCodes = [productTypeCode];
  const productName = filters.productName.trim();
  if (productName) body.productName = productName;
  const ntin = filters.ntin.trim();
  if (ntin) body.ntin = ntin;
  if (isOperator) {
    const orgIinBin = filters.orgIinBin.trim();
    if (orgIinBin) body.orgIinBin = orgIinBin;
  }
  if (startDate) body.generationDateStart = format(startDate, "dd.MM.yyyy");
  if (endDate) {
    body.generationDateEnd = format(endDate, "dd.MM.yyyy");
  } else if (startDate) {
    body.generationDateEnd = format(startDate, "dd.MM.yyyy");
  }
  return body;
}

function mapOrgOptions(
  raw: any,
  locale: string,
  labelStyle: "nameFirst" | "iinFirst" = "nameFirst",
): OrgOption[] {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.content)
        ? raw.content
        : [];
  return list
    .map((org: any) => {
      const iinBin = String(org?.iinBin || org?.bin || org?.iin || "").trim();
      if (!iinBin) return null;
      const name = String(
        locale === "kk"
          ? org?.nameKk || org?.nameRu || org?.name || ""
          : org?.nameRu || org?.nameKk || org?.name || "",
      )
        .replace(/\s+/g, " ")
        .trim();
      const label =
        labelStyle === "iinFirst"
          ? name
            ? `${iinBin} ${name}`
            : iinBin
          : name
            ? `${name} (${iinBin})`
            : iinBin;
      return { value: iinBin, label } as OrgOption;
    })
    .filter(Boolean) as OrgOption[];
}

function toIinFirstOption(option: OrgOption | undefined): OrgOption | null {
  if (!option) return null;
  const value = option.value;
  // если label уже "iin name" — оставить; если "name (iin)" — переложить
  const paren = option.label.match(/^(.*)\s+\(([^)]+)\)$/);
  if (paren && paren[2] === value) {
    return { value, label: `${value} ${paren[1].trim()}` };
  }
  if (option.label.startsWith(value)) return option;
  return { value, label: option.label.includes(value) ? option.label : `${value} ${option.label}` };
}

function toNameFirstOption(option: OrgOption | undefined): OrgOption | null {
  if (!option) return null;
  const value = option.value;
  if (option.label.startsWith(value + " ")) {
    const name = option.label.slice(value.length).trim();
    return { value, label: name ? `${name} (${value})` : value };
  }
  return option;
}

export default function MarkingCodesList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = normalizedLocale(i18n.language);
  const userData: any = useAppSelector((state) => state.auth.userData);
  const isOperator = Array.isArray(userData?.selectRoleV2)
    ? userData.selectRoleV2.some(
        (role: { roleId: number }) => role.roleId === OPERATOR_ROLE_ID,
      )
    : false;
  const [loading, setLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState<any>(null);
  const [marks, setMarks] = useState<MarkRegistryItem[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const scanHandledRef = useRef(false);
  const [initialValues, setInitialValues] = useState<FilterValues>(emptyFilters());
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [orgNameOptions, setOrgNameOptions] = useState<OrgOption[]>([]);
  const [orgIinOptions, setOrgIinOptions] = useState<OrgOption[]>([]);
  const [ntinNameOptions, setNtinNameOptions] = useState<SelectOption[]>([]);
  const orgNameSearchRef = useRef(0);
  const orgIinSearchRef = useRef(0);
  const ntinNameSearchRef = useRef(0);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [childrenMap, setChildrenMap] = useState<
    Record<number, MarkRegistryItem[]>
  >({});

  const statusOptions: SelectOption[] = [
    {
      value: "ISSUED",
      label: t("documents_file_upload.code_status_issued"),
    },
    {
      value: "INFLICTED",
      label: t("documents_file_upload.code_status_inflicted"),
    },
    {
      value: "IN_CIRCULATION",
      label: t("documents_file_upload.code_status_in_circulation"),
    },
    {
      value: "WITHDRAWN_FROM_CIRCULATION",
      label: t("documents_file_upload.code_status_withdrawn"),
    },
    {
      value: "AGGREGATED",
      label: t("marking_codes.status_aggregated"),
    },
    {
      value: "DEAGGREGATED",
      label: t("marking_codes.status_deaggregated"),
    },
  ];

  const kmTypeOptions: SelectOption[] = [
    { value: "CONSUMER", label: t("marking_codes.km_type_consumer") },
    { value: "GROUP", label: t("marking_codes.km_type_group") },
  ];

  const load = async (
    pageIndex = pagination.pageIndex,
    pageSize = pagination.pageSize,
    overrides?: {
      filters?: FilterValues;
      startDate?: Date | undefined;
      endDate?: Date | undefined;
    },
  ) => {
    setLoading(true);
    try {
      const body = buildSearchBody(
        overrides?.filters ?? initialValues,
        overrides && "startDate" in overrides
          ? overrides.startDate
          : startDate,
        overrides && "endDate" in overrides ? overrides.endDate : endDate,
        isOperator,
      );
      const res = await MarksRegistryService.searchMarksRegistry(
        body,
        pageIndex + 1,
        pageSize,
      );
      setMarks(res?.marks || []);
      setTotalItems(res?.totalItems || 0);
      setTotalPages(res?.totalPages || 0);
      setExpanded({});
      setChildrenMap({});

      if (
        Object.keys(body).length > 0 &&
        (!res?.marks || res.marks.length === 0)
      ) {
        setAlertOpen({
          status: "error",
          message: t("marking_codes.not_found"),
        });
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errorMessage ||
        t("marking_codes.load_error");
      const status = e?.response?.status;
      if (status === 403 || /принадлежит|другой орган/i.test(String(msg))) {
        setAlertOpen({
          status: "error",
          message: t("marking_codes.foreign_org"),
        });
      } else {
        setAlertOpen({ status: "error", message: msg });
      }
      setMarks([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [pagination.pageIndex, pagination.pageSize]);

  const applyMarkCodeSearch = (rawCode: string) => {
    const markCode = rawCode.trim();
    if (markCode && markCode.length !== MARK_CODE_LENGTH) {
      setAlertOpen({
        status: "error",
        message: t("marking_codes.mark_code_length_error"),
      });
      return;
    }
    const nextFilters: FilterValues = { ...initialValues, markCode };
    setInitialValues(nextFilters);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    load(0, pagination.pageSize, { filters: nextFilters });
  };

  const markCodeInvalid =
    !!initialValues.markCode.trim() &&
    initialValues.markCode.trim().length !== MARK_CODE_LENGTH;

  const handleScanResult = (result: string) => {
    if (scanHandledRef.current) return;
    scanHandledRef.current = true;
    setIsScannerOpen(false);
    applyMarkCodeSearch(result);
  };

  const searchOrgByName = useCallback(
    async (inputValue: string) => {
      const query = (inputValue || "").trim();
      if (query.length < 3) {
        setOrgNameOptions([]);
        return;
      }
      const requestId = ++orgNameSearchRef.current;
      try {
        const result = await searchOrganisationByName(query);
        if (requestId !== orgNameSearchRef.current) return;
        setOrgNameOptions(mapOrgOptions(result, locale, "nameFirst"));
      } catch {
        if (requestId !== orgNameSearchRef.current) return;
        setOrgNameOptions([]);
      }
    },
    [locale],
  );

  const searchOrgByIin = useCallback(
    async (inputValue: string) => {
      const query = (inputValue || "").trim();
      if (query.length < 3) {
        setOrgIinOptions([]);
        return;
      }
      const requestId = ++orgIinSearchRef.current;
      try {
        const result = await searchByIin(query);
        if (requestId !== orgIinSearchRef.current) return;
        setOrgIinOptions(mapOrgOptions(result, locale, "iinFirst"));
      } catch {
        if (requestId !== orgIinSearchRef.current) return;
        setOrgIinOptions([]);
      }
    },
    [locale],
  );

  const debouncedOrgNameSearch = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      return (value: string) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchOrgByName(value);
        }, 300);
      };
    })(),
    [searchOrgByName],
  );

  const debouncedOrgIinSearch = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      return (value: string) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchOrgByIin(value);
        }, 300);
      };
    })(),
    [searchOrgByIin],
  );

  const searchNtinByName = useCallback(async (inputValue: string) => {
    const query = (inputValue || "").trim();
    if (query.length < 3) {
      setNtinNameOptions([]);
      return;
    }
    const requestId = ++ntinNameSearchRef.current;
    try {
      const result = await getNtinCode(query);
      if (requestId !== ntinNameSearchRef.current) return;
      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];
      const options = list
        .map((item: any) => {
          const ntin = String(item?.ntin || "").trim();
          if (!ntin) return null;
          const name = String(
            item?.name || item?.nameRu || item?.nameKz || item?.gtinName || "",
          )
            .replace(/\s+/g, " ")
            .trim();
          return {
            value: ntin,
            label: name ? `${name} (${ntin})` : ntin,
          } as SelectOption;
        })
        .filter(Boolean) as SelectOption[];
      setNtinNameOptions(options);
    } catch {
      if (requestId !== ntinNameSearchRef.current) return;
      setNtinNameOptions([]);
    }
  }, []);

  const debouncedNtinNameSearch = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      return (value: string) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchNtinByName(value);
        }, 300);
      };
    })(),
    [searchNtinByName],
  );

  const applyOrgSelection = (
    formik: any,
    iinBin: string,
    source: "name" | "iin",
  ) => {
    const nextIin = iinBin || "";
    formik.setFieldValue("orgName", nextIin);
    formik.setFieldValue("orgIinBin", nextIin);
    setInitialValues((prev) => ({
      ...prev,
      orgName: nextIin,
      orgIinBin: nextIin,
    }));
    if (!nextIin) {
      setOrgNameOptions([]);
      setOrgIinOptions([]);
      return;
    }
    const fromName = orgNameOptions.find((o) => o.value === nextIin);
    const fromIin = orgIinOptions.find((o) => o.value === nextIin);
    const picked = source === "name" ? fromName || fromIin : fromIin || fromName;
    const nameOpt = toNameFirstOption(picked);
    const iinOpt = toIinFirstOption(picked);
    setOrgNameOptions(nameOpt ? [nameOpt] : []);
    setOrgIinOptions(iinOpt ? [iinOpt] : []);
  };

  const orgNameNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if ((inputValue || "").trim().length < 3) {
      return t("marking_codes.filter_org_name_hint");
    }
    return t("marking_codes.filter_org_name_empty");
  };

  const orgIinNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if ((inputValue || "").trim().length < 3) {
      return t("marking_codes.filter_org_iin_hint");
    }
    return t("marking_codes.filter_org_iin_empty");
  };

  const ntinNameNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if ((inputValue || "").trim().length < 3) {
      return t("marking_codes.filter_ntin_name_hint");
    }
    return t("marking_codes.filter_ntin_name_empty");
  };

  const applyNtinFromName = (formik: any, selectedNtin: string) => {
    const nextNtin = selectedNtin || "";
    formik.setFieldValue("ntin", nextNtin);
    formik.setFieldValue("ntinName", nextNtin);
    setInitialValues((prev) => ({
      ...prev,
      ntin: nextNtin,
      ntinName: nextNtin,
    }));
    if (!nextNtin) {
      setNtinNameOptions([]);
      return;
    }
    const picked = ntinNameOptions.find((o) => o.value === nextNtin);
    if (picked) setNtinNameOptions([picked]);
  };

  const applyNtinManual = (formik: any, nextNtin: string) => {
    // любое ручное изменение NTIN — селектор наименования больше не совпадает
    formik.setFieldValue("ntin", nextNtin);
    formik.setFieldValue("ntinName", "");
    setNtinNameOptions([]);
    setInitialValues((prev) => ({
      ...prev,
      ntin: nextNtin,
      ntinName: "",
    }));
  };

  const toggleExpand = async (row: MarkRegistryItem, e?: MouseEvent) => {
    e?.stopPropagation();
    const markId = Number(row.id);
    if (!Number.isFinite(markId)) return;

    if (expanded[markId]) {
      setExpanded((prev) => ({ ...prev, [markId]: false }));
      return;
    }

    setExpanded((prev) => ({ ...prev, [markId]: true }));
    if (markId in childrenMap) return;

    try {
      const res = await MarksRegistryService.getMarkChildren(markId);
      const list = Array.isArray(res)
        ? res
        : res?.marks || res?.data || [];
      setChildrenMap((prev) => ({ ...prev, [markId]: list }));
    } catch {
      setChildrenMap((prev) => ({ ...prev, [markId]: [] }));
    }
  };

  const renderFilterInputs = () => (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        const markCode = String(values.markCode || "").trim();
        if (markCode && markCode.length !== MARK_CODE_LENGTH) {
          setAlertOpen({
            status: "error",
            message: t("marking_codes.mark_code_length_error"),
          });
          return;
        }
        const nextFilters: FilterValues = {
          ...values,
          markCode,
          status: normalizeMulti(values.status),
          kmTypes: normalizeMulti(values.kmTypes),
        };
        setInitialValues(nextFilters);
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setIsFilterOpen(false);
        load(0, pagination.pageSize, {
          filters: nextFilters,
          startDate,
          endDate,
        });
      }}
      validateOnBlur
      validateOnChange
    >
      {(formik) => (
        <Form className="w-[560px] max-w-[90vw] m-t-[20px]">
          <div className="flex flex-row align-center justify-between">
            <p className="my-3 text-lg font-semibold">
              {t("documents_upload.filters")}
            </p>
            <button type="button" onClick={() => setIsFilterOpen(false)}>
              <img src="/icons/close.svg" alt="" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.col_code")}
                </label>
              </div>
              <Field
                type="text"
                name="markCode"
                className={style.placeholder}
                placeholder={t("marking_codes.search_placeholder")}
                maxLength={MARK_CODE_LENGTH}
                value={formik.values.markCode}
                onChange={(e: any) => {
                  const markCode = e.target.value;
                  formik.setFieldValue("markCode", markCode);
                  setInitialValues((prev) => ({ ...prev, markCode }));
                }}
              />
              {formik.values.markCode.trim() &&
              formik.values.markCode.trim().length !== MARK_CODE_LENGTH ? (
                <div className="text-red-500 text-xs mt-1">
                  {t("marking_codes.mark_code_length_error")}
                </div>
              ) : null}
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.col_status")}
                </label>
              </div>
              <Field
                value={formik.values.status || []}
                component={SelectCustomFormik}
                name="status"
                placeholder={t("documentLabels.choose")}
                size="dm"
                isClearable
                isMulti
                options={statusOptions}
                onChange={(values: unknown) => {
                  const next = normalizeMulti(values);
                  formik.setFieldValue("status", next);
                  setInitialValues((prev) => ({
                    ...prev,
                    status: next,
                  }));
                }}
              />
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.col_km_type")}
                </label>
              </div>
              <Field
                value={formik.values.kmTypes || []}
                component={SelectCustomFormik}
                name="kmTypes"
                placeholder={t("documentLabels.choose")}
                size="dm"
                isClearable
                isMulti
                options={kmTypeOptions}
                onChange={(values: unknown) => {
                  const next = normalizeMulti(values);
                  formik.setFieldValue("kmTypes", next);
                  setInitialValues((prev) => ({
                    ...prev,
                    kmTypes: next,
                  }));
                }}
              />
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.col_product_type_code")}
                </label>
              </div>
              <Field
                type="text"
                name="productTypeCode"
                className={style.placeholder}
                placeholder={t("documentLabels.choose_option")}
                value={formik.values.productTypeCode}
                onChange={(e: any) => {
                  formik.setFieldValue("productTypeCode", e.target.value);
                  setInitialValues((prev) => ({
                    ...prev,
                    productTypeCode: e.target.value,
                  }));
                }}
              />
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.filter_product_name")}
                </label>
              </div>
              <Field
                type="text"
                name="productName"
                className={style.placeholder}
                placeholder={t("documentLabels.choose_option")}
                value={formik.values.productName}
                onChange={(e: any) => {
                  formik.setFieldValue("productName", e.target.value);
                  setInitialValues((prev) => ({
                    ...prev,
                    productName: e.target.value,
                  }));
                }}
              />
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>NTIN</label>
              </div>
              <Field
                type="text"
                name="ntin"
                className={style.placeholder}
                placeholder={t("documentLabels.choose_option")}
                value={formik.values.ntin}
                onChange={(e: any) => {
                  applyNtinManual(formik, e.target.value);
                }}
              />
            </div>

            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("marking_codes.filter_ntin_name")}
                </label>
              </div>
              <Field
                key={`ntin-name-${formik.values.ntinName || "empty"}`}
                value={formik.values.ntinName || ""}
                component={SelectCustomFormik}
                name="ntinName"
                placeholder={t("marking_codes.filter_ntin_name_hint")}
                size="dm"
                isClearable
                isMulti={false}
                options={ntinNameOptions}
                onInputChange={(value: string) => {
                  debouncedNtinNameSearch(value);
                }}
                noOptionsMessage={ntinNameNoOptionsMessage}
                onChange={(selectedNtin: string) => {
                  applyNtinFromName(formik, selectedNtin || "");
                }}
              />
            </div>

            {isOperator && (
              <>
                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("marking_codes.filter_org_name")}
                    </label>
                  </div>
                  <Field
                    value={formik.values.orgName || ""}
                    component={SelectCustomFormik}
                    name="orgName"
                    placeholder={t("marking_codes.filter_org_name_hint")}
                    size="dm"
                    isClearable
                    isMulti={false}
                    options={orgNameOptions}
                    onInputChange={(value: string) => {
                      debouncedOrgNameSearch(value);
                    }}
                    noOptionsMessage={orgNameNoOptionsMessage}
                    onChange={(iinBin: string) => {
                      applyOrgSelection(formik, iinBin, "name");
                    }}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("marking_codes.filter_org_iin_bin")}
                    </label>
                  </div>
                  <Field
                    value={formik.values.orgIinBin || ""}
                    component={SelectCustomFormik}
                    name="orgIinBin"
                    placeholder={t("marking_codes.filter_org_iin_hint")}
                    size="dm"
                    isClearable
                    isMulti={false}
                    options={orgIinOptions}
                    onInputChange={(value: string) => {
                      debouncedOrgIinSearch(value);
                    }}
                    noOptionsMessage={orgIinNoOptionsMessage}
                    onChange={(iinBin: string) => {
                      applyOrgSelection(formik, iinBin, "iin");
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-center">
            <Calendar
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              label={t("marking_codes.filter_date")}
            />
          </div>

          <div className="flex flex-row gap-[10px] mt-2">
            <button
              type="submit"
              className="bg-green text-white font-semibold py-2 px-4 rounded"
            >
              {t("mainButtons3.apply")}
            </button>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => {
                const cleared = emptyFilters();
                setStartDate(undefined);
                setEndDate(undefined);
                setInitialValues(cleared);
                setOrgNameOptions([]);
                setOrgIinOptions([]);
                setNtinNameOptions([]);
                formik.resetForm({ values: cleared });
                setPagination((p) => ({ ...p, pageIndex: 0 }));
                setIsFilterOpen(false);
                load(0, pagination.pageSize, {
                  filters: cleared,
                  startDate: undefined,
                  endDate: undefined,
                });
              }}
            >
              {t("mainButtons3.reset")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderDataCells = (
    row: MarkRegistryItem,
    indexLabel: string | number,
    depth: number,
  ) => {
    const markId = Number(row.id);
    const group = isGroupMark(row);
    const isOpen = !!expanded[markId];
    const st = getMarkStatusType(row.status || "", t);

    return (
      <>
        <td className={tdClass}>{indexLabel}</td>
        <td
          className={tdCodeClass}
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          {row.markCode}
        </td>
        <td className={tdClass}>{row.productTypeCode || "—"}</td>
        <td className={tdClass}>{row.productTypeName || "—"}</td>
        <td className={tdClass}>
          <div className="inline-flex items-center justify-center gap-1">
            <span>{row.ntin || "—"}</span>
            <CopyButton value={row.ntin} />
          </div>
        </td>
        <td className={tdClass}>{row.gtinName || row.ntinName || "—"}</td>
        <td className={tdClass}>{row.generatedAt || "—"}</td>
        <td className={tdClass}>
          <div className="inline-flex items-center justify-center gap-1">
            {group && (
              <button
                type="button"
                aria-label="expand"
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-400 text-gray-600 transition-transform duration-200 hover:bg-gray-100 ${
                  isOpen ? "rotate-90" : ""
                }`}
                onClick={(e) => toggleExpand(row, e)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 2L8 6L4 10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <span>{formatKmType(row.kmType, t)}</span>
          </div>
        </td>
        <td className={tdClass}>
          {row.childMarksCount != null ? row.childMarksCount : "—"}
        </td>
        <td className={tdClass}>
          <div className="flex justify-center">
            <StatusBadge type={st.type}>{st.label}</StatusBadge>
          </div>
        </td>
        <td className={tdClass}>{row.statusAssignedAt || "—"}</td>
        <td className={tdClass}>{row.withdrawalReason || "—"}</td>
        <td className={tdClass}>{row.ownerIinBin || "—"}</td>
        <td className={tdClass}>{row.issuerIinBin || "—"}</td>
      </>
    );
  };

  const renderRowTree = (
    row: MarkRegistryItem,
    indexLabel: string | number,
    depth: number,
  ): ReactElement[] => {
    const markId = Number(row.id);
    const nodes: ReactElement[] = [
      <tr
        key={`row-${markId}-${depth}-${indexLabel}`}
        className="cursor-pointer hover:bg-gray-100"
        onClick={() => navigate(`/marking-codes/${row.id}`)}
      >
        {renderDataCells(row, indexLabel, depth)}
      </tr>,
    ];

    if (!expanded[markId] || !(markId in childrenMap)) return nodes;

    const children = childrenMap[markId] || [];
    if (!children.length) {
      nodes.push(
        <tr key={`empty-${markId}-${depth}`}>
          <td
            colSpan={COL_SPAN}
            className="px-5 py-4 text-sm border-b border-gray-200 text-gray-500 text-center"
          >
            {t("documents_orders.children_empty")}
          </td>
        </tr>,
      );
      return nodes;
    }

    children.forEach((child, i) => {
      nodes.push(...renderRowTree(child, `${indexLabel}.${i + 1}`, depth + 1));
    });
    return nodes;
  };

  return (
    <div
      className="flex flex-1 flex-col min-w-0"
      onClick={() => isFilterOpen && setIsFilterOpen(false)}
    >
      <h2 className="my-3 text-xl font-semibold">{t("marking_codes.title")}</h2>

      <div className={style.toolbar} onClick={(e) => e.stopPropagation()}>
        <div className={style.searchFieldWrap}>
          <div className={style.searchField}>
            <input
              type="text"
              className={style.searchFieldInput}
              placeholder={t("marking_codes.search_placeholder")}
              value={initialValues.markCode}
              maxLength={MARK_CODE_LENGTH}
              onChange={(e) => {
                const markCode = e.target.value;
                setInitialValues((prev) => ({ ...prev, markCode }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyMarkCodeSearch(e.currentTarget.value);
                }
              }}
            />
            <button
              type="button"
              className={style.searchScanBtn}
              title={t("qrScaner.mainButton")}
              aria-label={t("qrScaner.mainButton")}
              onClick={() => {
                scanHandledRef.current = false;
                setIsScannerOpen(true);
              }}
            >
              <img src="/icons/datamatrix.svg" alt="" />
            </button>
          </div>
          {initialValues.markCode.trim() ? (
            <button
              type="button"
              className={style.searchFindBtn}
              onClick={() => applyMarkCodeSearch(initialValues.markCode)}
            >
              {t("marking_codes.find")}
            </button>
          ) : null}
        </div>
        {markCodeInvalid ? (
          <div className="basis-full w-full text-red-500 text-sm order-last">
            {t("marking_codes.mark_code_length_error")}
          </div>
        ) : null}

        <div className={style.toolbarActions}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(true);
            }}
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            data-tooltip-offset={4}
            data-tooltip-id="marks-registry-filter"
          >
            {t("mainButtons3.filter")}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const cleared = emptyFilters();
              setStartDate(undefined);
              setEndDate(undefined);
              setInitialValues(cleared);
              setOrgNameOptions([]);
              setOrgIinOptions([]);
              setNtinNameOptions([]);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
              load(0, pagination.pageSize, {
                filters: cleared,
                startDate: undefined,
                endDate: undefined,
              });
            }}
          >
            <img src="/icons/update.svg" alt="" />
          </button>
        </div>
      </div>

      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className={thClass}>№</th>
                <th className={`${thClass} !max-w-[160px] min-w-[140px]`}>
                  {t("marking_codes.col_code")}
                </th>
                <th className={thClass}>
                  {t("marking_codes.col_product_type_code")}
                </th>
                <th className={thClass}>
                  {t("marking_codes.col_product_type_name")}
                </th>
                <th className={thClass}>NTIN</th>
                <th className={thClass}>{t("marking_codes.col_ntin_name")}</th>
                <th className={thClass}>{t("marking_codes.col_generated_at")}</th>
                <th className={thClass}>{t("marking_codes.col_km_type")}</th>
                <th className={thClass}>{t("marking_codes.child_count")}</th>
                <th className={thClass}>{t("marking_codes.col_status")}</th>
                <th className={thClass}>
                  {t("marking_codes.col_status_assigned_at")}
                </th>
                <th className={thClass}>
                  {t("marking_codes.col_withdrawal_reason")}
                </th>
                <th className={thClass}>{t("marking_codes.col_owner_iin")}</th>
                <th className={thClass}>{t("marking_codes.col_issuer_iin")}</th>
              </tr>
            </thead>
            {!marks.length ? (
              <tbody>
                <tr>
                  <td colSpan={COL_SPAN} className="text-center py-8">
                    <EmptyListPlaceholderContent
                      title={t("marking_codes.no_data_title")}
                      description={t("marking_codes.no_data_description")}
                    />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {marks.flatMap((row, index) =>
                  renderRowTree(
                    row,
                    pagination.pageIndex * pagination.pageSize + index + 1,
                    0,
                  ),
                )}
              </tbody>
            )}
          </table>
        </div>
        {totalPages > 0 && (
          <Pagination
            totalItems={totalItems}
            totalPages={totalPages}
            pagination={pagination}
            setPagination={setPagination}
            pageSizeOptions={MARKS_PAGE_SIZE_OPTIONS}
            skipPersistPageSize
          />
        )}
      </div>

      <div className="z-[1000] relative" onClick={(e) => e.stopPropagation()}>
        <Tooltip
          isOpen={isFilterOpen}
          id="marks-registry-filter"
          className={classNames(style.wrapperTooltipFilter, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
        </Tooltip>
      </div>

      {loading ? <Loader /> : null}
      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
      {isScannerOpen ? (
        <QrScannerComponent
          onClose={() => setIsScannerOpen(false)}
          onScanResult={handleScanResult}
        />
      ) : null}
    </div>
  );
}
